import { defineStore } from "pinia";

import {
  type Anstalldarende,
  Arbetsgivarearende,
  ArendenByMedarbetareRequest,
  type IKundserviceAppApiClient,
} from "@/composables/KundserviceAppApiClient";

interface MinaArendenState {
  isLoading: boolean;
  error: string | null;
  openAnstalldarenden: {
    arenden: Anstalldarende[];
    localPage: number;
    remotePage: number;
    isLoading: boolean;
  };
  closedAnstalldarenden: {
    arenden: Anstalldarende[];
    localPage: number;
    remotePage: number;
    isLoading: boolean;
  };
  openArbetsgivarearenden: {
    arenden: Arbetsgivarearende[];
    nextLink?: string | null;
    isLoading: boolean;
  };
  closedArbetsgivarearenden: {
    arenden: Arbetsgivarearende[];
    nextLink?: string | null;
    isLoading: boolean;
  };
}
const DEFAULT_PAGE_SIZE = 5;
const REMOTE_PAGE_SIZE = 30;

const isOnLastLocalPage = (localPage: number) =>
  (localPage * DEFAULT_PAGE_SIZE) % REMOTE_PAGE_SIZE === 0;

export const useMinaArendenStore = defineStore("minaArenden", {
  state: (): MinaArendenState => ({
    isLoading: false,
    error: null,
    openAnstalldarenden: {
      arenden: [],
      localPage: 1,
      remotePage: 1,
      isLoading: false,
    },
    closedAnstalldarenden: {
      arenden: [],
      localPage: 1,
      remotePage: 1,
      isLoading: false,
    },
    openArbetsgivarearenden: {
      arenden: [],
      nextLink: null,
      isLoading: false,
    },
    closedArbetsgivarearenden: {
      arenden: [],
      nextLink: null,
      isLoading: false,
    },
  }),
  getters: {
    getOpenArenden: (state) => {
      return (
        state.openAnstalldarenden?.arenden.slice(
          0,
          state.openAnstalldarenden.localPage * DEFAULT_PAGE_SIZE,
        ) ?? []
      );
    },
    getCanFetchMoreOpenArenden: (state) => {
      return (
        state.openAnstalldarenden.arenden.length >
          state.openAnstalldarenden.localPage * DEFAULT_PAGE_SIZE ||
        isOnLastLocalPage(state.openAnstalldarenden.localPage)
      );
    },
    getClosedArenden: (state) => {
      return (
        state.closedAnstalldarenden?.arenden.slice(
          0,
          state.closedAnstalldarenden.localPage * DEFAULT_PAGE_SIZE,
        ) ?? []
      );
    },
    getCanFetchMoreClosedArenden: (state) => {
      return (
        state.closedAnstalldarenden.arenden.length >
          state.closedAnstalldarenden.localPage * DEFAULT_PAGE_SIZE ||
        isOnLastLocalPage(state.closedAnstalldarenden.localPage)
      );
    },
  },
  actions: {
    async initFetch(apiClient: IKundserviceAppApiClient) {
      try {
        this.$state.isLoading = true;
        const [
          openAnstalldarendenResponse,
          closedAnstalldarendenResponse,
          openArbetsgivarearendenResponse,
          closedArbetsgivarearendenRseponse,
        ] = await Promise.all([
          apiClient.getAnstalldarendenByMedarbetare(true, 1),
          apiClient.getAnstalldarendenByMedarbetare(false, 1),
          apiClient.getArbetsgivarearendenByMedarbetare({
            open: true,
            top: DEFAULT_PAGE_SIZE,
            nextLink: undefined,
          } as ArendenByMedarbetareRequest),
          apiClient.getArbetsgivarearendenByMedarbetare({
            open: false,
            top: DEFAULT_PAGE_SIZE,
            nextLink: undefined,
          } as ArendenByMedarbetareRequest),
        ]);

        this.$patch((store) => {
          store.openAnstalldarenden.arenden =
            openAnstalldarendenResponse.value ?? [];
          store.closedAnstalldarenden.arenden =
            closedAnstalldarendenResponse.value ?? [];

          store.openArbetsgivarearenden.arenden =
            openArbetsgivarearendenResponse.value ?? [];
          store.closedArbetsgivarearenden.arenden =
            closedArbetsgivarearendenRseponse.value ?? [];
          store.openArbetsgivarearenden.nextLink =
            openArbetsgivarearendenResponse.nextLink;
          store.closedArbetsgivarearenden.nextLink =
            closedArbetsgivarearendenRseponse.nextLink;

          store.isLoading = false;
        });
      } catch {
        this.$patch((store) => {
          store.error =
            "Något gick fel när vi hämtade dina ärenden. Försök igen senare.";
          store.isLoading = false;
        });
      }
    },
    async fetchAnstalldarenden(
      open: boolean,
      apiClient: IKundserviceAppApiClient,
    ) {
      const arendeType = open ? "openAnstalldarenden" : "closedAnstalldarenden";

      if (!isOnLastLocalPage(this.$state[arendeType].localPage)) {
        this.$state[arendeType].localPage++;
        return;
      }

      this.$state[arendeType].isLoading = true;

      try {
        const arenden = await apiClient.getAnstalldarendenByMedarbetare(
          open,
          this.$state[arendeType].remotePage + 1,
        );

        const sortedArenden = this.$state[arendeType].arenden
          .concat(arenden.value ?? [])
          .sort((a, b) => b.updatedAt!.getTime() - a.updatedAt!.getTime());

        this.$patch((store) => {
          store[arendeType].remotePage++;
          store[arendeType].localPage++;
          store[arendeType].arenden = sortedArenden;
        });
      } catch (error) {
        console.error("Error fetching anstalldarenden.", error);
      } finally {
        this.$state[arendeType].isLoading = false;
      }
    },
    async fetchArbetsivarearenden(
      open: boolean,
      apiClient: IKundserviceAppApiClient,
    ) {
      const arendeType = open
        ? "openArbetsgivarearenden"
        : "closedArbetsgivarearenden";
      try {
        this.$state[arendeType].isLoading = true;

        const arendenResponse =
          await apiClient.getArbetsgivarearendenByMedarbetare({
            open,
            top: DEFAULT_PAGE_SIZE,
            nextLink: this.$state[arendeType].nextLink ?? undefined,
          } as ArendenByMedarbetareRequest);

        this.$patch((store) => {
          store[arendeType].arenden.push(...arendenResponse.value);
          store[arendeType].nextLink = arendenResponse.nextLink;
        });
      } catch (error) {
        console.error("Error fetching arbetsgivarearenden.", error);
      } finally {
        this.$state[arendeType].isLoading = false;
      }
    },
  },
});
